import { Grid } from '@mui/material';
import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '../../../components/LoadingButton';
import { Modal } from '../../../components/Modal';
import { EventTrackTypes } from '../../../constants/EventTracks';
import * as routing from '../../../constants/Routing';
import { Route } from '../../../domain/route/Route';
import { useCaptureEvents } from '../../../hooks/useCaptureEvents';
import { useDriversState } from '../../../hooks/useDriversState';
import { RouteNotification } from '../home/notifications/RouteNotification';
import { useNotification } from '../hooks/useNotification';
import { useRouteContext } from '../route/context/context';
import { useAcceptOptimization } from '../route/stops/hooks/useAcceptOptimization';
import { NewRouteNotificationMap } from './NewRouteNotificationMap';

export const NotificationDialog = () => {
  const { capture } = useCaptureEvents();
  const { notification, clearNotification: clearNotificationsDialog } = useNotification();
  const { driversState } = useDriversState();
  const { routeState } = useRouteContext();
  const { run, isLoading, success } = useAcceptOptimization();
  const navigate = useNavigate();

  useEffect(() => {
    if (success && notification) {
      const newRoute = notification.data.route as Route;
      const nextStop = newRoute.stops.find((stop) => stop.status === `pending`)?.id ?? ``;

      navigate(routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, newRoute.id).replace(`:stopId`, nextStop));
      clearNotificationsDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  if (
    !notification ||
    ![
      `smartmonkey.planner.2.planner.stop.deleted`,
      `smartmonkey.planner.2.planner.stop.created`,
      `smartmonkey.planner.2.planner.stop.rescheduled`,
      `smartmonkey.planner.2.planner.stop.moved`,
      `smartmonkey.planner.2.planner.driver.deleted`,
      `smartmonkey.planner.2.optimizer.find_new_route_optimization.succeeded`,
    ].includes(notification.id)
  )
    return null;

  if (notification.id === `smartmonkey.planner.2.optimizer.find_new_route_optimization.succeeded` && routeState.route) {
    const newRoute = notification.data.route as Route;
    const height = (driversState.height > window.innerHeight ? window.innerHeight : driversState.height) - 258;
    if (!notification.data.route.estimated_end_time) return;
    const timeDiffInMin = routeState.route.estimated_end_time
      ? Math.ceil(
          (new Date(routeState.route.estimated_end_time).getTime() -
            new Date(newRoute.estimated_end_time as Date).getTime()) /
            (1000 * 60)
        )
      : 0;
    if (!(timeDiffInMin > 0)) return;

    return (
      <RouteNotification
        open
        onClose={(reason) => {
          switch (reason) {
            case `discard`:
              capture({
                type: EventTrackTypes.SuggestBestRoute,
                payload: {
                  [`Action`]: `Discard notification`,
                },
              });
              break;
            case `timeout`:
              capture({
                type: EventTrackTypes.SuggestBestRoute,
                payload: {
                  [`Action`]: `Timeout discard`,
                },
              });
              break;
            default:
              break;
          }
          !isLoading && clearNotificationsDialog();
        }}
        header={{ intlId: `drivers.notifications.header.recommend` }}
        subtitle={{
          // text: ``,drivers.notifications.header.recommend.new_route_after_starting // For the future
          intlId: `drivers.notifications.header.recommend.new_route_after_report`,
          intlValues: {
            num: timeDiffInMin,
          },
          containerStyle: {
            height: 51,
            marginBottom: 0,
            display: `flex`,
            alignItems: `center`,
          },
        }}
        expandableContent={
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ height: `100%`, width: `100%`, flexWrap: `nowrap` }}
          >
            <NewRouteNotificationMap selectedRoute={notification.data.route} height={height} />
            <VSpacer medium />

            <LoadingButton
              intlId="drivers.notifications.content_has_changed.update"
              disabled={isLoading}
              onClick={() => {
                if (isLoading) return;
                capture({
                  type: EventTrackTypes.SuggestBestRoute,
                  payload: {
                    [`Action`]: `Accept`,
                  },
                });
                run({ route_id: notification.data.driver_id, log_id: notification.data.logEntry_id });
              }}
              style={{ width: `100%`, boxSizing: `border-box` }}
            />
            <LoadingButton
              disabled={isLoading}
              variant="flat"
              intlId="drivers.notifications.content_has_changed.not_now"
              onClick={() => {
                capture({
                  type: EventTrackTypes.SuggestBestRoute,
                  payload: {
                    [`Action`]: `Reject`,
                  },
                });
                clearNotificationsDialog();
              }}
              style={{ width: `100%`, boxSizing: `border-box`, border: `none` }}
            />
          </Grid>
        }
      />
    );
  }

  return (
    <Modal
      open
      onClose={() => clearNotificationsDialog()}
      title={{
        intlId: `drivers.notifications.content_has_changed.title`,
      }}
      subtitle={{
        intlId: `drivers.notifications.content_has_changed.subtitle`,
      }}
      // isLoading,
      buttons={[
        {
          intlId: `drivers.notifications.content_has_changed.not_now`,
          onClick: () => clearNotificationsDialog(),
          variant: `text`,
          style: {
            border: `none`,
          },
        },
        {
          intlId: `drivers.notifications.content_has_changed.update`,
          onClick: () => window.location.reload(),
        },
      ]}
    />
  );
};
